import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useModule } from '../contexts/ModuleContext';
import { Zap, Check, AlertCircle, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ['Travel (Flight/Train)', 'Stay (Hotel)', 'Food', 'Local Transport', 'Client Expense', 'Personal Vehicle', 'Miscellaneous'];
const PAYMENT_MODES = ['Personal', 'Company Cash', 'Company Card'];

const QuickAddExpense = () => {
  const { user } = useAuth();
  const { activeModule } = useModule();
  const navigate = useNavigate();
  const role = user?.role;
  const effectiveModule = role === 'accounts' ? 'INTERNATIONAL' : (role === 'dgm' || role === 'senior_approver') ? 'DOMESTIC' : activeModule;
  const effectiveCurrency = effectiveModule === 'DOMESTIC' ? 'INR' : 'USD';
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [dupWarning, setDupWarning] = useState(false);
  const [limitWarning, setLimitWarning] = useState('');
  const [billFile, setBillFile] = useState(null);
  const [trips, setTrips] = useState([]);
  const [perKmRate, setPerKmRate] = useState(10);

  const [form, setForm] = useState({
    category: 'Food',
    vendor: '',
    amount: '',
    expense_date: new Date().toISOString().split('T')[0],
    payment_mode: 'Personal',
    description: '',
    trip_id: '',
    trip_name: '',
    kilometres_travelled: ''
  });

  const isPersonalVehicle = form.category === 'Personal Vehicle';
  const calculatedAmount = isPersonalVehicle && form.kilometres_travelled
    ? (parseFloat(form.kilometres_travelled) * perKmRate).toFixed(2)
    : '';

  useEffect(() => {
    fetchMyTrips();
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      if (res.data?.personal_vehicle_rate_per_km) setPerKmRate(res.data.personal_vehicle_rate_per_km);
    } catch (err) { /* ignore */ }
  };

  const fetchMyTrips = async () => {
    try {
      const res = await axios.get(`${API}/trips`, { params: { status: 'ongoing' } });
      setTrips(res.data);
    } catch (err) { /* ignore */ }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'trip_id') {
      const sel = trips.find(t => t.id === value);
      setForm(prev => ({ ...prev, trip_id: value, trip_name: sel?.trip_name || '' }));
    } else if (name === 'category') {
      setForm(prev => ({ ...prev, category: value, kilometres_travelled: '', amount: value === 'Personal Vehicle' ? '' : prev.amount }));
    } else {
      setForm(prev => ({ ...prev, [name]: value }));
    }

    // Check duplicate on key fields
    if (['amount', 'expense_date', 'vendor'].includes(name)) {
      const updatedForm = { ...form, [name]: value };
      if (updatedForm.amount && updatedForm.expense_date && updatedForm.vendor) {
        checkDuplicate(updatedForm);
      }
    }

    // Check limit warning
    if (name === 'amount' || name === 'category') {
      const updatedForm = { ...form, [name]: value };
      checkLimit(updatedForm.category, parseFloat(updatedForm.amount) || 0);
    }
  };

  const checkDuplicate = async (f) => {
    try {
      const res = await axios.get(`${API}/expenses/check-duplicate`, {
        params: { employee_id: user.id, amount: f.amount, expense_date: f.expense_date, vendor: f.vendor }
      });
      setDupWarning(res.data.is_duplicate);
    } catch { setDupWarning(false); }
  };

  const checkLimit = (category, amount) => {
    const limits = { 'Food': 2000, 'Stay (Hotel)': 8000, 'Local Transport': 3000 };
    const limit = limits[category];
    if (limit && amount > limit) {
      setLimitWarning(`Unusually high expense for ${category} (daily limit: INR ${limit.toLocaleString()}). Please review.`);
    } else {
      setLimitWarning('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.vendor || !billFile) {
      setError('Vendor and bill are required');
      return;
    }
    if (isPersonalVehicle) {
      if (!form.kilometres_travelled || parseFloat(form.kilometres_travelled) <= 0) {
        setError('Enter valid kilometres for Personal Vehicle');
        return;
      }
    } else if (!form.amount || parseFloat(form.amount) <= 0) {
      setError('Amount is required');
      return;
    }
    setLoading(true);
    try {
      const data = new FormData();
      data.append('employee_id', user.id);
      data.append('employee_name', user.name);
      data.append('department', user.department || '');
      data.append('category', form.category);
      data.append('vendor', form.vendor);
      data.append('amount', isPersonalVehicle ? calculatedAmount : form.amount);
      data.append('expense_date', form.expense_date);
      data.append('payment_mode', form.payment_mode);
      data.append('brand_division', 'General / Office');
      data.append('description', form.description || `${form.category} - ${form.vendor}`);
      data.append('expense_type', effectiveModule);
      data.append('currency', effectiveCurrency);
      if (isPersonalVehicle && form.kilometres_travelled) {
        data.append('kilometres_travelled', form.kilometres_travelled);
      }
      if (form.trip_id) {
        data.append('trip_id', form.trip_id);
        data.append('trip_name', form.trip_name);
      }
      data.append('bill', billFile);
      await axios.post(`${API}/expenses`, data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setForm({ category: 'Food', vendor: '', amount: '', expense_date: new Date().toISOString().split('T')[0], payment_mode: 'Personal', description: '', trip_id: '', trip_name: '', kilometres_travelled: '' });
        setBillFile(null);
        setDupWarning(false);
        setLimitWarning('');
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-md mx-auto">
        <div className="flex items-center gap-2 mb-6">
          <Zap className="w-5 h-5 text-amber-500" />
          <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Quick Add Expense</h1>
        </div>

        {success && (
          <div className="card bg-emerald-50 border-emerald-200 text-center py-6 mb-4" data-testid="quick-add-success">
            <Check className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
            <p className="text-sm font-medium text-emerald-700">Expense Added!</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* Warnings */}
          {dupWarning && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2" data-testid="duplicate-warning">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-700">Possible duplicate expense detected (same amount, date, vendor)</p>
            </div>
          )}
          {limitWarning && (
            <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-start gap-2" data-testid="limit-warning">
              <AlertCircle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-orange-700">{limitWarning}</p>
            </div>
          )}

          {/* Trip */}
          {trips.length > 0 && (
            <select name="trip_id" value={form.trip_id} onChange={handleChange} className="input-field" data-testid="quick-trip-select">
              <option value="">No Trip (Standalone)</option>
              {trips.map(t => <option key={t.id} value={t.id}>{t.trip_name}</option>)}
            </select>
          )}

          <div className="grid grid-cols-2 gap-3">
            <select name="category" value={form.category} onChange={handleChange} className="input-field" data-testid="quick-category">
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <select name="payment_mode" value={form.payment_mode} onChange={handleChange} className="input-field" data-testid="quick-payment">
              {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>

          <input name="vendor" value={form.vendor} onChange={handleChange} placeholder="Vendor name" className="input-field" data-testid="quick-vendor" />

          {/* Personal Vehicle: km input */}
          {isPersonalVehicle ? (
            <div className="space-y-2">
              <input name="kilometres_travelled" type="number" value={form.kilometres_travelled} onChange={handleChange} placeholder="Kilometres travelled" className="input-field" step="0.1" min="0.1" data-testid="quick-km" />
              <div className="input-field bg-gray-50 text-gray-700 font-semibold text-sm" data-testid="quick-calc-amount">
                {calculatedAmount ? `₹${Number(calculatedAmount).toLocaleString('en-IN')} (₹${perKmRate}/km)` : 'Enter km to calculate'}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <input name="amount" type="number" value={form.amount} onChange={handleChange} placeholder={`Amount (${effectiveCurrency})`} className="input-field" step="0.01" min="0.01" data-testid="quick-amount" />
              <input name="expense_date" type="date" value={form.expense_date} onChange={handleChange} className="input-field" data-testid="quick-date" />
            </div>
          )}

          {isPersonalVehicle && (
            <input name="expense_date" type="date" value={form.expense_date} onChange={handleChange} className="input-field" data-testid="quick-date-pv" />
          )}

          <input name="description" value={form.description} onChange={handleChange} placeholder="Short note (optional)" className="input-field" data-testid="quick-description" />

          {!billFile ? (
            <label className="block cursor-pointer">
              <div className="border-2 border-dashed border-gray-200 rounded-xl p-4 text-center hover:border-indigo-400 transition-all">
                <input type="file" onChange={(e) => setBillFile(e.target.files[0])} accept="image/*,.pdf" className="hidden" data-testid="quick-file" />
                <p className="text-sm text-gray-500">Tap to upload bill</p>
              </div>
            </label>
          ) : (
            <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <span className="text-sm text-emerald-700 truncate">{billFile.name}</span>
              <button type="button" onClick={() => setBillFile(null)} className="text-gray-400 hover:text-red-500 text-xs">Remove</button>
            </div>
          )}

          {error && <p className="text-red-600 text-sm" data-testid="quick-error">{error}</p>}

          <button type="submit" disabled={loading} className="btn btn-primary w-full justify-center py-3" data-testid="quick-submit">
            {loading ? 'Submitting...' : 'Submit Expense'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default QuickAddExpense;
